import authService from './authService';
import { 
  Provider, 
  ProviderExtended,
  ProviderFormData, 
  ProviderSearchModel,
  ProviderWithProducts
} from '../types/provider';
import { PaginatedResponse, ApiResponse } from '../types/common';
import type { ProviderBankCounterpart } from '../types/provider';
import type {
  PaymentMethodOption,
  ProviderPaymentTerm,
  SaveProviderPaymentTermRequest,
} from '../types/providerPaymentTerm';
import { apiErrorFrom } from '../utils/apiError';

class ProviderService {
  constructor() {
    // Configuration will be loaded lazily when needed
  }

  /**
   * Get authenticated API client
   */
  private async getApiClient() {
    return authService.getApiClient();
  }

  /**
   * List providers with pagination
   */
  async getProviders(searchModel: ProviderSearchModel): Promise<PaginatedResponse<Provider>> {
    const params = new URLSearchParams({
      page: searchModel.page.toString(),
      limit: searchModel.pageSize.toString(),
      ...(searchModel.searchDocument && { document: searchModel.searchDocument }),
      ...(searchModel.searchEmail && { email: searchModel.searchEmail }),
      ...(searchModel.state && { state: searchModel.state }),
      ...(searchModel.personType && { personType: searchModel.personType.toString() }),
      ...(searchModel.orderBy && { orderBy: searchModel.orderBy }),
      ...(searchModel.orderDirection && { orderDirection: searchModel.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    
    // Se tem termo de busca, usa o endpoint de busca
    if (searchModel.searchName) {
      const searchParams = new URLSearchParams({
        q: searchModel.searchName,
        page: searchModel.page.toString(),
        page_size: searchModel.pageSize.toString()
      });
      const response = await apiClient.get(`/providers/search?${searchParams}`);
      
      if (!response.ok) {
        throw new Error('Failed to search providers');
      }
      
      const json = await response.json();
      return {
        success: json.success,
        data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
        pagination: {
          page: json.data?.pagination?.page ?? 1,
          limit: json.data?.pagination?.page_size ?? searchModel.pageSize,
          total: json.data?.pagination?.total_items ?? 0,
          totalPages: json.data?.pagination?.total_pages ?? 1,
        }
      };
    }
    
    // Senão usa o endpoint de listagem
    const response = await apiClient.get(`/providers?${params}`);

    if (!response.ok) {
      throw new Error('Failed to fetch providers');
    }

    const json = await response.json();
    return {
      success: json.success,
      data: Array.isArray(json.data?.data) ? json.data.data : (Array.isArray(json.data) ? json.data : []),
      pagination: {
        page: json.data?.pagination?.page ?? 1,
        limit: json.data?.pagination?.page_size ?? searchModel.pageSize,
        total: json.data?.pagination?.total_items ?? 0,
        totalPages: json.data?.pagination?.total_pages ?? 1,
      }
    };
  }

  /**
   * Map API Provider fields to ProviderExtended fields used by the detail view
   */
  private mapToExtended(p: Provider): ProviderExtended {
    return {
      ...p,
      federal_tax_id: p.document,
      state_tax_id: p.state_registration_tax_id,
      company_name: p.legal_name,
      trade_name: p.trade_name,
      address: p.addr_street,
      address_number: p.addr_num,
      complement: p.addr_compl,
      neighborhood: p.addr_district,
      city: p.addr_city ?? '',
      state: p.addr_state ?? '',
      postal_code: p.addr_zip,
      is_active: p.is_active,
    };
  }

  /** GET /providers/:id/bank-counterparts */
  async getBankCounterparts(id: number): Promise<ProviderBankCounterpart[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/${id}/bank-counterparts`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao carregar contrapartes bancárias');
    const data: ApiResponse<ProviderBankCounterpart[]> = await response.json();
    return data.data ?? [];
  }

  /** POST /providers/:id/bank-counterparts */
  async addBankCounterpart(id: number, body: { document: string; name?: string }): Promise<ProviderBankCounterpart> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/providers/${id}/bank-counterparts`, body);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao cadastrar contraparte bancária');
    const data: ApiResponse<ProviderBankCounterpart> = await response.json();
    return data.data!;
  }

  /** GET /providers/:id/payment-term — condição (null = sem) e as formas de pagamento. */
  async getPaymentTerm(id: number): Promise<{ term: ProviderPaymentTerm | null; paymentMethods: PaymentMethodOption[] }> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/${id}/payment-term`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao carregar a condição de pagamento');
    const data: ApiResponse<{ term: ProviderPaymentTerm | null; payment_methods: PaymentMethodOption[] }> = await response.json();
    return { term: data.data?.term ?? null, paymentMethods: data.data?.payment_methods ?? [] };
  }

  /** PUT /providers/:id/payment-term */
  async savePaymentTerm(id: number, body: SaveProviderPaymentTermRequest): Promise<ProviderPaymentTerm> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/providers/${id}/payment-term`, body);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao salvar a condição de pagamento');
    const data: ApiResponse<ProviderPaymentTerm> = await response.json();
    return data.data!;
  }

  /** DELETE /providers/:id/payment-term */
  async deletePaymentTerm(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/providers/${id}/payment-term`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao remover a condição de pagamento');
  }

  /** DELETE /providers/:id/bank-counterparts/:counterpartId */
  async removeBankCounterpart(id: number, counterpartId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/providers/${id}/bank-counterparts/${counterpartId}`);
    if (!response.ok) throw await apiErrorFrom(response, 'Erro ao remover contraparte bancária');
  }

  /**
   * Get provider by ID
   */
  async getProviderById(id: number): Promise<ProviderExtended> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Fornecedor não encontrado');
      }
      throw new Error('Failed to fetch provider');
    }

    const data: ApiResponse<Provider> = await response.json();
    return this.mapToExtended(data.data!);
  }

  /**
   * Get provider with related products
   */
  async getProviderWithProducts(id: number): Promise<ProviderWithProducts> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/${id}/products`); // ✨ Endpoint atualizado

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Fornecedor não encontrado');
      }
      throw new Error('Failed to fetch provider with products');
    }

    const data: ApiResponse<ProviderWithProducts> = await response.json();
    return data.data!;
  }

  /**
   * Create provider
   */
  async createProvider(providerData: ProviderFormData): Promise<Provider> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/providers', providerData);

    if (!response.ok) {
      if (response.status === 409) {
        throw new Error(`Fornecedor com documento ${providerData.document} já existe`);
      }
      throw new Error('Failed to create provider');
    }

    const data: ApiResponse<Provider> = await response.json();
    return data.data!;
  }
  /**
   * Update provider
   */
  async updateProvider(id: number, providerData: Partial<ProviderFormData>): Promise<Provider> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/providers/${id}`, providerData);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Fornecedor não encontrado');
      }
      if (response.status === 409) {
        throw new Error('Documento ou email já existe');
      }
      throw new Error('Failed to update provider');
    }

    const data: ApiResponse<Provider> = await response.json();
    return data.data!;
  }

  /**
   * Delete provider
   */
  async deleteProvider(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/providers/${id}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Fornecedor não encontrado');
      }
      throw new Error('Failed to delete provider');
    }
  }

  /**
   * Search providers by term
   */
  async searchProviders(query: string): Promise<Provider[]> {
    const params = new URLSearchParams({ q: query });
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/providers/search?${params}`);

    if (!response.ok) {
      throw new Error('Failed to search providers');
    }

    const data: ApiResponse<Provider[]> = await response.json();
    return data.data!;
  }

  /**
   * Validar documento (CPF/CNPJ)
   */
  validateDocument(document: string): { isValid: boolean; type: 'CPF' | 'CNPJ' | null; formatted: string } {
    const cleaned = document.replace(/\D/g, '');
    
    if (cleaned.length === 11) {
      // CPF
      const isValid = this.validateCPF(cleaned);
      const formatted = isValid ? this.formatCPF(cleaned) : document;
      return { isValid, type: 'CPF', formatted };
    } else if (cleaned.length === 14) {
      // CNPJ
      const isValid = this.validateCNPJ(cleaned);
      const formatted = isValid ? this.formatCNPJ(cleaned) : document;
      return { isValid, type: 'CNPJ', formatted };
    }
    
    return { isValid: false, type: null, formatted: document };
  }

  private validateCPF(cpf: string): boolean {
    if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
    
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf[i]) * (10 - i);
    }
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;
    
    if (parseInt(cpf[9]) !== digit1) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf[i]) * (11 - i);
    }
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;
    
    return parseInt(cpf[10]) === digit2;
  }

  private validateCNPJ(cnpj: string): boolean {
    if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;
    
    const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const weights2 = [6, 7, 8, 9, 2, 3, 4, 5, 6, 7, 8, 9];
    
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(cnpj[i]) * weights1[i];
    }
    let remainder = sum % 11;
    const digit1 = remainder < 2 ? 0 : 11 - remainder;
    
    if (parseInt(cnpj[12]) !== digit1) return false;
    
    sum = 0;
    for (let i = 0; i < 13; i++) {
      sum += parseInt(cnpj[i]) * weights2[i];
    }
    remainder = sum % 11;
    const digit2 = remainder < 2 ? 0 : 11 - remainder;
    
    return parseInt(cnpj[13]) === digit2;
  }

  private formatCPF(cpf: string): string {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  private formatCNPJ(cnpj: string): string {
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
}

// Singleton instance
export const providerService = new ProviderService();
export default providerService;